import React, { useState } from "react";
import { bulkLookup, downloadBulkExport } from "@/lib/api";
import { fmtQty, fmtMoney, fmtNum, fmtCover, fmtLt } from "@/lib/format";
import { statusMeta, actionClass } from "@/lib/statusMeta";
import { stageClass } from "@/pages/InventoryAdvisor";
import { useItemDetail } from "@/lib/itemDetail";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

const CHIPS = [
  ["ALL", "All"], ["PO", "PO Required"], ["NOPO", "No PO Required"], ["STOCK_OUT", "Stock Out"],
  ["CRITICAL", "Critical"], ["POTENTIAL_STOCKOUT", "Potential Stockout"], ["OVERSTOCKED", "Overstocked"], ["NON_MOVER", "Non-Mover"],
];
const EXAMPLE = "11407\n15198\n3036\n9459\n27734";

export default function BulkItemLookup() {
  const { openItem } = useItemDetail();
  const [codes, setCodes] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [poOnly, setPoOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (f = filter, po = poOnly) => {
    if (!codes.trim()) { toast.error("Paste one or more Item Codes"); return; }
    setLoading(true);
    try {
      const res = await bulkLookup({ codes, filter: f, po_only: po });
      setData(res);
    } catch (e) { toast.error("Lookup failed"); } finally { setLoading(false); }
  };

  const rows = data?.rows || [];

  return (
    <div className="p-6 space-y-4">
      <div>
        <div className="label-eyebrow">PO Advisor</div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Bulk Item Lookup</h1>
        <p className="text-sm text-slate-500 mt-0.5">Paste Item Codes from Excel — the key question: does each Item Code need a new PO?</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        <div className="kcard p-4 space-y-3 h-fit">
          <label className="label-eyebrow">Item Codes (one per line or comma-separated)</label>
          <textarea value={codes} onChange={(e) => setCodes(e.target.value)} data-testid="bulk-codes"
            rows={12} placeholder={EXAMPLE}
            className="w-full rounded-md border border-[var(--line)] p-3 text-[13px] num outline-none focus:border-slate-400 slim resize-none" />
          <div className="flex items-center justify-between">
            <button className="text-[12px] text-slate-500 hover:text-slate-800" onClick={() => setCodes(EXAMPLE)} data-testid="bulk-example">Use example</button>
            <label className="flex items-center gap-2 text-[12px] text-slate-600">
              <Switch checked={poOnly} onCheckedChange={(v) => { setPoOnly(v); if (data) run(filter, v); }} data-testid="bulk-po-only" /> Show only items requiring PO
            </label>
          </div>
          <button onClick={() => run()} disabled={loading} data-testid="bulk-lookup-btn"
            className="w-full h-10 rounded-md text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--kcsa-red)" }}>
            <Search className="w-4 h-4" /> {loading ? "Looking up…" : "Look Up Items"}
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {CHIPS.map(([k, l]) => (
              <button key={k} onClick={() => { setFilter(k); if (data) run(k, poOnly); }} data-testid={`chip-${k}`}
                className={`px-3 h-8 rounded-full text-[12px] font-medium border ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-[var(--line)] hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>

          {data && (
            <div className="flex items-center justify-between">
              <div className="flex gap-4 text-[13px] text-slate-600">
                <span>Requested <b className="num text-slate-900">{fmtNum(data.requested)}</b></span>
                <span>Found <b className="num text-slate-900">{fmtNum(data.found)}</b></span>
                {data.not_found.length > 0 && <span className="text-amber-600">Not found <b className="num">{data.not_found.length}</b></span>}
              </div>
              <button onClick={() => downloadBulkExport({ codes, filter, po_only: poOnly })} data-testid="bulk-export"
                className="h-9 px-3 rounded-md border border-[var(--line)] bg-white text-[13px] font-medium text-slate-700 flex items-center gap-1.5"><Download className="w-4 h-4" /> Export</button>
            </div>
          )}

          <div className="space-y-2.5">
            {!data && <div className="kcard p-12 text-center text-slate-400 text-sm">Paste Item Codes and run a lookup to see PO recommendations.</div>}
            {data && rows.length === 0 && <div className="kcard p-12 text-center text-slate-400 text-sm">No matching items.</div>}
            {rows.map((r, i) => {
              const s = statusMeta(r.status);
              return (
                <div key={i} className="kcard p-4" style={{ borderLeft: `3px solid ${s.accent}` }} data-testid={`bulk-card-${r.item_no}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <button className="num text-lg font-bold text-[var(--kcsa-red-dark)] hover:underline" onClick={() => openItem(r.item_no)} data-testid={`bulk-item-${r.item_no}`}>{r.item_no}</button>
                        <Badge variant="outline" className={`${s.badge} rounded-full text-[11px]`}>{s.label}</Badge>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${r.po_required ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>PO {r.po_required ? "REQUIRED" : "NOT REQUIRED"}</span>
                        {r.proc?.primary_token && (
                          <span title={r.proc.primary_label} data-testid={`bulk-stage-${r.item_no}`}
                            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${stageClass(r.proc.primary_token)}`}>
                            Stage: {r.proc.primary_token}
                          </span>
                        )}
                      </div>
                      <div className="text-[13px] text-slate-700 mt-1 font-medium">{r.desc}</div>
                      <div className="text-[12px] text-slate-400 mt-0.5 truncate" title={r.warehouses_str}>{r.klass} · {r.warehouses_str}</div>
                    </div>
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded whitespace-nowrap ${actionClass(r.recommended_action)}`}>{r.recommended_action}</span>
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-x-4 gap-y-2 mt-3">
                    <Metric label="SOH" value={fmtQty(r.soh)} />
                    <Metric label="Open PO" value={fmtQty(r.open_po)} />
                    <Metric label="In Receipt" value={fmtQty(r.in_receipt)} />
                    <Metric label="In Transit" value={fmtQty(r.in_transit)} />
                    <Metric label="Total Supply" value={fmtQty(r.total_supply)} strong />
                    <Metric label="Avg Cons/mo" value={fmtQty(r.consumption)} />
                    <Metric label="Eff LT" value={fmtLt(r.effective_lt)} />
                    <Metric label="SOH Cover" value={fmtCover(r.soh_months_cover)} />
                    <Metric label="Supply Cover" value={fmtCover(r.supply_months_cover)} />
                  </div>

                  <div className="flex items-stretch gap-3 mt-3 flex-wrap">
                    <div className={`flex-1 min-w-[260px] rounded-md border p-2.5 text-[12.5px] leading-relaxed ${s.badge}`}>{r.reason}</div>
                    {r.proc?.po_qty != null && (
                      <div className="rounded-md bg-sky-50 border border-sky-200 px-3 py-2 flex items-start gap-4" data-testid={`bulk-po-info-${r.item_no}`}>
                        <div><div className="label-eyebrow">On PO Qty</div><div className="num text-base font-bold text-sky-700">{fmtQty(r.proc.po_qty)}</div></div>
                        <div><div className="label-eyebrow">ETA</div><div className="num text-[13px] font-semibold text-sky-800 whitespace-nowrap">{r.proc.po_eta || "—"}</div></div>
                        <div className="min-w-0">
                          <div className="label-eyebrow">Supplier{(r.proc.po_suppliers || []).length > 1 ? "s" : ""}</div>
                          {(r.proc.po_suppliers || []).length ? (
                            <ul className="mt-0.5 space-y-0.5">
                              {(r.proc.po_suppliers || []).map((s, si) => (
                                <li key={si} className="text-[12px] font-medium text-sky-800 leading-snug break-words max-w-[260px]">{s}</li>
                              ))}
                            </ul>
                          ) : <div className="text-[12.5px] font-medium text-sky-800">—</div>}
                        </div>
                      </div>
                    )}
                    {r.po_required && (
                      <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 flex items-center gap-4">
                        <div><div className="label-eyebrow">Rec PO Qty</div><div className="num text-base font-bold text-red-700">{fmtQty(r.recommended_po_qty)}</div></div>
                        <div><div className="label-eyebrow">Rec PO Value</div><div className="num text-base font-bold text-red-700">{fmtMoney(r.recommended_po_value)}</div></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, strong }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className={`num text-[13px] mt-0.5 ${strong ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>{value}</div>
    </div>
  );
}
